import { Button } from "@mui/material";
import { useState } from "react";
import { FloorWizardChooseLayoutStep } from "src/pages/PlannerPage/FloorWizard/FloorWizardChooseLayoutStep";
import { FloorWizardUploadLayoutStep } from "src/pages/PlannerPage/FloorWizard/FloorWizardUploadLayoutStep";
import { FloorWizardRoomCreationMethodStep } from "src/pages/PlannerPage/FloorWizard/FloorWizardRoomCreationMethodStep";
import { FloorWizardTutorialStep } from "src/pages/PlannerPage/FloorWizard/FloorWizardTutorialStep";
import { ts } from "src/utils/translate";
import { theme } from "src/theme/mui-theme";
import { FloorFormData, FloorLayout, isFloorUploadStepValid, RoomCreationMethod, useUpsertFloor } from "src/pages/PlannerPage/FloorWizard/useUpsertFloor";
import { StepConfig, StepDialog } from "src/pages/PlannerPage/FloorWizard/StepDialog";
import { useIsTectlyAiEnabledQuery } from "src/api/v1/feature-ApiSlice";
import { RecognitionContext } from "src/pages/PlannerPage/RoomRecognition/recognitionTypes";

interface FloorWizardProps {
    projectId: number;
    onClose: () => void;
    onStartRecognition: (context: RecognitionContext) => void;
}

type WizardStep = 0 | 1 | 2 | 3;

export const FloorWizard = ({ projectId, onClose, onStartRecognition }: FloorWizardProps) => {
    const { upsertFloor, startScaleSetup } = useUpsertFloor(projectId, onClose);
    const { data: isTectlyAiEnabled } = useIsTectlyAiEnabledQuery();
    const roomCreationMethodStepEnabled = isTectlyAiEnabled ?? false;

    const [step, setStep] = useState<WizardStep>(0);
    const [formData, setFormData] = useState<FloorFormData>({
        selectedLayout: FloorLayout.UPLOAD,
        floorId: undefined,
        floorName: '',
        ceilingHeight: '270',
        dontShowAgain: false,
        planImageFile: null,
        planPreviewImageUrl: null,
        planPdfFile: null,
        planPdfPage: 1,
        roomCreationMethod: RoomCreationMethod.MANUAL,
    });

    const handleNext = () => setStep((s) => (s + 1) as WizardStep);
    const handleBack = () => setStep((s) => (s - 1) as WizardStep);

    const handleUploadLayoutNext = async () => {
        if (roomCreationMethodStepEnabled) {
            handleNext();
        } else {
            const floorId = await upsertFloor(formData);
            if (floorId == null) return;
            setFormData((prev) => ({ ...prev, floorId, planImageFile: null }));
            setStep(3);
        }
    };

    const handleRoomCreationMethodNext = async () => {
        const floorId = await upsertFloor(formData);
        if (floorId == null) return;
        setFormData((prev) => ({ ...prev, floorId, planImageFile: null }));

        if (formData.roomCreationMethod === RoomCreationMethod.AUTOMATIC) {
            onStartRecognition({
                projectId,
                floorId,
                isSampleImage: formData.selectedLayout !== FloorLayout.UPLOAD,
            });
            onClose();
        } else {
            handleNext();
        }
    };

    const getStepConfig = (): StepConfig => {
        switch (step) {
            case 0:
                return {
                    title: ts('choose_layout', 'Choose a layout'),
                    subtitle: ts('use_provided_flor_plan_or_upload_own', 'Use provided floor plan or upload your own'),
                    maxWidth: 'xs',
                    actionsLeft: (
                        <Button variant="outlined" sx={{ width: 120 }} onClick={onClose}>
                            {ts('q_sc_back', 'Back')}
                        </Button>
                    ),
                    actions: (
                        <Button variant="contained" onClick={handleNext} sx={{ width: 120 }}>
                            {ts('next_button_label', 'Next')}
                        </Button>
                    ),
                    body: <FloorWizardChooseLayoutStep formData={formData} setFormData={setFormData} />,
                };
            case 1: {
                const title = formData.selectedLayout === FloorLayout.UPLOAD
                    ? ts('planner_add_new_floor', 'Add new floor')
                    : ts('sample_layout', 'Sample Layout');

                const subtitle = formData.selectedLayout === FloorLayout.UPLOAD
                    ? ts('upload_layout', 'Upload layout')
                    : ts('edit_your_layout', 'Edit your layout');

                return {
                    title,
                    subtitle,
                    maxWidth: 'sm',
                    actionsLeft: (
                        <Button variant="outlined" onClick={handleBack} sx={{ width: 120 }}>
                            {ts('q_sc_back', 'Back')}
                        </Button>
                    ),
                    actions: (
                        <Button
                            variant="contained"
                            onClick={handleUploadLayoutNext}
                            disabled={!isFloorUploadStepValid(formData)}
                            sx={{ width: roomCreationMethodStepEnabled ? 120 : 180 }}
                        >
                            {roomCreationMethodStepEnabled
                                ? ts('next_button_label', 'Next')
                                : ts('set_reference_length', 'Set Reference Length')
                            }
                        </Button>
                    ),
                    body: <FloorWizardUploadLayoutStep projectId={projectId} formData={formData} setFormData={setFormData} />,
                };
            }
            case 2:
                return {
                    title: ts('room_creation_method', 'Room creation method'),
                    subtitle: ts('select_how_to_create_rooms', 'Select how you wish to create rooms in this project'),
                    maxWidth: 'sm',
                    actionsLeft: (
                        <Button variant="outlined" onClick={handleBack} sx={{ width: 120 }}>
                            {ts('q_sc_back', 'Back')}
                        </Button>
                    ),
                    actions: (
                        <Button variant="contained" onClick={handleRoomCreationMethodNext} sx={{ width: 120 }}>
                            {ts('continue_button_label', 'Continue')}
                        </Button>
                    ),
                    body: <FloorWizardRoomCreationMethodStep formData={formData} setFormData={setFormData} />,
                };
            case 3:
                return {
                    title: ts('you_can_easily_set_reference_length', 'You can easily set reference length'),
                    maxWidth: 'sm',
                    actions: (
                        <Button variant="contained" sx={{ width: 120 }} onClick={() => startScaleSetup(Number(formData.floorId))}>
                            {ts('set_reference_length_begin', 'Start')}
                        </Button>
                    ),
                    body: <FloorWizardTutorialStep formData={formData} setFormData={setFormData} />,
                };
        }
    };

    return <StepDialog config={getStepConfig()} onClose={onClose} titleColor={theme.palette.primary.main} />;
};